import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { IconButton, ListItem, TextField, makeStyles } from "@material-ui/core";
import { defineMessages, useIntl } from "react-intl";
import { useFormStyles } from "./formFields";
import { useEditPopupStyles } from "./EditPopup";
import { fetchAttachments, setFilesToUpload, removeAttachment } from "../../redux/attachments/attachmentsActions";
import {
    formatFileOnInput,
    convertByteArray,
    resizeFile,
    isValidImageExt,
    getSupportedFileExtensions,
} from "../common/attachments/attachmentsHelper.js";
import { showAlert, ALERT_TYPES } from "../../redux/alert/alertActions";
import AddIcon from "@material-ui/icons/Add";
import { AttachmentEntry } from "./attachmentEntry";
import { AttachmentEntryEdit } from "./attachmentEntryEdit";
import { Lightbox } from "../common/attachments/lightbox";

const messages = defineMessages({
    additionalDetails: {
        id: "task-complete-form.attachment-additional-details",
        defaultMessage: "Additional details",
        description: "Additional details",
    },
    addYourCommentsHere: {
        id: "task-complete-form.attachment-add-your-comments-here",
        defaultMessage: "Add your comments here",
        description: "Add your comments here label",
    },
    attachmentsCaption: {
        id: "task-complete-form.attachments-caption",
        defaultMessage: "Add picture",
        description: "Add picture label",
    },
    imageDetailsCaption: {
        id: "task-complete-form.attachments-details-caption",
        defaultMessage: "Image details",
        description: "Image details label",
    },
    videoError: {
        id: "task-complete-form.attachments-video-error",
        defaultMessage: "Videos are not supported",
        description: "Videos are not supported error message",
    },
    NoAcceptableFileError: {
        id: "admin.work-order-details.attachments-table.non-acceptable-file-error",
        defaultMessage: "Only valid files are supported",
        description: "Only valid files are supported",
    },
});

export const AttachmentsField = (props) => {
    const {
        workOrderNumber,
        clickDispatchId,
        acceptedTypes = [
            ".jpg",
            ".jpeg",
            ".png",
            ".xlsx",
            ".xls",
            ".csv",
            ".pdf",
            ".docx",
            ".doc",
            ".pptx",
            ".ppt",
            ".msg",
            ".txt",
            ".xml",
            ".jpe",
            ".jfif",
            ".gif",
            ".rtf",
            ".bmp",
            ".tif",
            ".tiff",
            ".heic",
            ".heif",
        ],
    } = props;
    const intl = useIntl();

    const [files, setFiles] = useState([]);
    const [open, setOpen] = useState(false);
    const [fileIndex, setFileIndex] = useState(null);
    const [disableUpload, setDisableUpload] = useState(false);
    const inputFile = useRef(null);

    const formClasses = useFormStyles();
    const popUpStyles = useEditPopupStyles();

    useEffect(() => {
        if (workOrderNumber) props.fetchAttachments(workOrderNumber);
    }, [workOrderNumber]);

    useEffect(() => {
        setFiles(props.workOrderAttachments);
    }, [props.workOrderAttachments]);

    const openFileSelect = () => {
        inputFile.current.click();
    };

    const removeNewRow = (id) => {
        var newSelectedFiles = props.filesToUpload.filter((f) => f.id !== id);
        props.setFilesToUpload(newSelectedFiles);
        setDisableUpload(newSelectedFiles.some((f) => !f.fileName || f.fileName.trim() === ""));
    };

    const handleAttachmentSelect = (index, alreadyUploaded) => {
        setFileIndex(alreadyUploaded ? index : index + files.length);
        setOpen(true);
    };

    const handleFileDelete = (file) => {
        const fileDetails = {
            id: file.id,
            workOrderNumber: props.workOrderNumber,
            payload: {
                createdDateTime: file.createdDateTime,
                id: file.id,
                newFileDescription: "",
                newFileName: "",
                newFileType: "",
                newRelatedJobId: "",
                newRelatedSourceId: "",
                oldFileDescription: file.fileDescription,
                oldFileName: file.fileName,
                oldFileType: file.fileType,
                oldRelatedJobId: file.relatedJobId,
                oldRelatedSourceId: file.relatedSourceId,
                originatingSystem: "CMIS",
                uploadedBy: props.userProfile.username,
            },
        };
        props.removeAttachment(fileDetails);
    };

    const handleFileSelection = async (e) => {
        const { files } = e.target;
        if (files && files.length > 0) {
            var formattedFiles = [];
            for (const fileRaw of files) {
                let file = fileRaw;
                if (file.type.includes("video")) {
                    props.showAlert(Date.now(), messages.videoError, ALERT_TYPES.error);
                    continue; // Skip videos
                }
                if (!file.name.includes(".") || !isValidImageExt(`.${file.name.split(".")[file.name.split(".").length - 1]}`)) {
                    props.showAlert(Date.now(), messages.NoAcceptableFileError, ALERT_TYPES.error);
                    continue;
                }
                if (file.type.includes("image") && file.size > 2000000) {
                    // If image size is above 2mb, compress
                    file = await resizeFile(file);
                }
                const data = await convertByteArray(file);
                var formattedFile = formatFileOnInput(file, data, clickDispatchId, workOrderNumber, props.userProfile.username);
                formattedFiles.push(formattedFile);
            }
            props.setFilesToUpload([...props.filesToUpload, ...formattedFiles]);
        }
    };

    let allFiles = files.concat(props.filesToUpload);
    return (
        <>
            <Lightbox open={open} mobile onClose={() => setOpen(false)} files={allFiles} fileIndex={fileIndex} />
            {files?.map((file, i) => (
                <AttachmentEntry
                    key={file.id}
                    file={file}
                    handleAttachmentSelect={() => handleAttachmentSelect(i, true)}
                    characterCountEnabled
                    onRemove={() => {
                        handleFileDelete(file);
                    }}
                />
            ))}
            {props.filesToUpload?.map((file, i) => (
                <AttachmentEntryEdit
                    key={file.id}
                    file={file}
                    handleAttachmentSelect={() => handleAttachmentSelect(i)}
                    editorProps={{
                        title: intl.formatMessage(messages.imageDetailsCaption),
                        editor: (props) => (
                            <TextField
                                id={"attachment-details-" + file.id}
                                placeholder={intl.formatMessage(messages.addYourCommentsHere)}
                                multiline
                                fullWidth
                                rows={4}
                                variant="outlined"
                                InputProps={{
                                    inputProps: { maxLength: 300 },
                                }}
                                {...props}
                            />
                        ),
                    }}
                    onChange={(file) => {
                        let fileIndex = props.filesToUpload.findIndex((f) => f.id === file.id);
                        if (fileIndex >= 0) {
                            let copy = [...props.filesToUpload];
                            copy[fileIndex] = file;
                            // State update won't trigger rerender on submit button component, unless
                            // we make a clone of the array. Make a clone if the file name is an empty
                            // string so state will update. Only do this when file name is empty,
                            // or the first time it's not empty after clearing the file name
                            if (file.fileName.trim() === "" || disableUpload) {
                                setDisableUpload(!disableUpload);
                                copy = [...copy];
                            }

                            props.setFilesToUpload(copy);
                        }
                    }}
                    removeNewRow={removeNewRow}
                    characterCountEnabled
                />
            ))}
            <ListItem>
                <div className={formClasses.formElement}>
                    <div className={formClasses.formInfo}>
                        <div className={formClasses.infoHeader}>{intl.formatMessage(messages.attachmentsCaption)}</div>
                    </div>
                    <input
                        style={{ display: "none" }}
                        multiple
                        ref={inputFile}
                        onChange={handleFileSelection}
                        onClick={(event) => (event.target.value = null)}
                        type="file"
                        accept={getSupportedFileExtensions().toString()}
                    />
                    <div className={popUpStyles.formElementRoot + " " + popUpStyles.formElement}>
                        <IconButton aria-describedby={"open-file-upload"} type="button" className={popUpStyles.formBtn} onClick={openFileSelect}>
                            <AddIcon color="primary" />
                        </IconButton>
                    </div>
                </div>
            </ListItem>
        </>
    );
};

const mapStateToProps = (state, prevProps) => {
    return {
        workOrderAttachments: state.attachments.workOrderAttachments,
        filesToUpload: state.attachments.filesToUpload,
        userProfile: state.login.userProfile,
    };
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            fetchAttachments,
            setFilesToUpload,
            removeAttachment,
            showAlert,
        },
        dispatch,
    );

export default connect(mapStateToProps, mapDispatchToProps)(AttachmentsField);
